"use client";

import { useState } from "react";
import type { AiProfile } from "@/types/ai";
import { db } from "@/lib/db";
import { eventBus } from "@/lib/utils/eventBus";
import AccountPanel from "@/components/settings/AccountPanel";
import MindalooApiKeysPanel from "@/components/settings/MindalooApiKeysPanel";

interface Props {
  profile: AiProfile;
  onProfileUpdate: (profile: AiProfile) => void;
}

// Personality, appearance (colors, typography, chat bubbles, animated
// backgrounds), and behavior all moved to the Studio (MindalooAgentStudio,
// opened via "finetune") — this used to duplicate them here as six
// separate tabs, most of them literal "coming soon" placeholders (Fonts &
// Colors, Bubbles, Chat Ring, Ultra Nav, Tiles), plus a Branding tab that
// was a third, different color picker overlapping both the placeholders
// and the Studio. One real, complete editor beats six half-built ones.
type TabId = "account" | "apikeys" | "general" | "knowledge";

const TABS: { id: TabId; label: string }[] = [
  { id: "account", label: "Account" },
  { id: "apikeys", label: "API Keys" },
  { id: "general", label: "General" },
  { id: "knowledge", label: "Knowledge" },
];

const card = "rounded-2xl border border-white/[0.07] bg-white/[0.04] p-5";
const cardLabel = "text-xs font-semibold text-white/50 uppercase tracking-widest mb-1 block";

export default function SettingsManager({ profile, onProfileUpdate }: Props) {
  const [activeTab, setActiveTab] = useState<TabId>("account");
  const [agentModeEnabled, setAgentModeEnabled] = useState(profile.agentModeEnabled);

  const accent = profile.brand?.primaryColor || "#C1A2FF";
  const secondary = profile.brand?.secondaryColor || "#9333EA";

  async function handleToggleAgentMode() {
    if (profile.plan === "free") {
      eventBus.emit("TOAST:SHOW", { message: "Agent Mode is a Pro feature. Upgrade to unlock it.", type: "info" });
      return;
    }
    const updated = { ...profile, agentModeEnabled: !agentModeEnabled, updatedAt: Date.now() };
    await db.aiProfiles.update(profile.id, updated);
    setAgentModeEnabled(!agentModeEnabled);
    onProfileUpdate(updated);
  }

  const openStudio = () => eventBus.emit("BUILDER:OPEN");

  return (
    <div className="h-full overflow-y-auto scrollbar-hide text-white">
      <div className="p-4 space-y-4">
        {/* Tab bar */}
        <div className="flex gap-1 overflow-x-auto scrollbar-hide -mx-1 px-1">
          {TABS.map((t) => {
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all shrink-0 ${
                  active ? "text-white" : "text-white/40 hover:text-white/70"
                }`}
                style={active ? {
                  background: `linear-gradient(135deg, ${accent}30, ${secondary}20)`,
                  border: `1px solid ${accent}35`,
                } : { border: "1px solid transparent" }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* AccountPanel and MindalooApiKeysPanel render their own
            "premium-white-glass" cards (near-opaque white/light background)
            regardless of the dark panel around them — the previous version
            of this file forced white text onto those same light cards,
            which is invisible white-on-white. Passing dark text colors
            (matching what the cards actually render as) is what makes the
            content legible; it's a deliberate light-card-on-dark-backdrop
            look, not a mismatch. */}
        {activeTab === "account" && (
          <AccountPanel
            profilePlan={profile.plan}
            primaryColor={accent}
            secondaryColor={secondary}
            textColor="#18181b"
            textSecondaryColor="rgba(0,0,0,0.65)"
          />
        )}

        {activeTab === "apikeys" && (
          <div className="space-y-4">
            <p className="text-sm text-white/50">
              Your own API keys for OpenAI, Anthropic, Gemini, and other providers — used anywhere you'd use ChatGPT, Claude, or Gemini directly.
            </p>
            <MindalooApiKeysPanel
              textColor="#18181b"
              textSecondaryColor="rgba(0,0,0,0.65)"
              primaryColor={accent}
              secondaryColor={secondary}
            />
          </div>
        )}

        {activeTab === "general" && (
          <div className="space-y-4">
            <div className={card}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-white/90">Agent Mode</p>
                  <p className="text-xs text-white/40 mt-0.5">Enable tool calling and agent capabilities</p>
                </div>
                <button
                  type="button"
                  onClick={handleToggleAgentMode}
                  disabled={profile.plan === "free"}
                  className={`relative w-11 h-6 rounded-full transition-colors ${profile.plan === "free" ? "opacity-40 cursor-not-allowed" : ""} ${agentModeEnabled ? "" : "bg-white/10"}`}
                  style={agentModeEnabled ? { background: `linear-gradient(135deg, ${accent}, ${secondary})` } : {}}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${agentModeEnabled ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>
            </div>

            <div className={card}>
              <label className={cardLabel}>Profile</label>
              <div className="space-y-2 text-sm mt-2">
                <div className="flex justify-between"><span className="text-white/40">Name</span><span className="text-white/90 font-medium">{profile.name}</span></div>
                <div className="flex justify-between"><span className="text-white/40">Mode</span><span className="text-white/90 font-medium capitalize">{profile.modePreset}</span></div>
                <div className="flex justify-between"><span className="text-white/40">Privacy</span><span className="text-white/90 font-medium">{profile.privacyMode === "strict" ? "Strict" : "Smart"}</span></div>
                <div className="flex justify-between"><span className="text-white/40">Plan</span><span className="text-white/90 font-medium capitalize">{profile.plan}</span></div>
              </div>
            </div>

            <button
              type="button"
              onClick={openStudio}
              className="w-full flex items-center justify-between px-5 py-4 rounded-2xl text-left transition"
              style={{ background: `linear-gradient(135deg, ${accent}20, ${secondary}14)`, border: `1px solid ${accent}30` }}
            >
              <div>
                <p className="text-sm font-semibold text-white/90">Fine-tune in Studio</p>
                <p className="text-xs text-white/50 mt-0.5">Identity, personality, appearance & behavior — full editor</p>
              </div>
              <span className="text-white/40">→</span>
            </button>
          </div>
        )}

        {activeTab === "knowledge" && (
          <div className={card + " space-y-4"}>
            <div>
              <p className="text-sm font-semibold text-white/90 mb-1">Knowledge Base</p>
              <p className="text-xs text-white/40">Upload files, connect folders, and give your AI access to your documents.</p>
            </div>
            <a
              href="/knowledge"
              className="inline-flex px-4 py-2 rounded-xl text-xs font-semibold text-white transition"
              style={{ background: `linear-gradient(135deg, ${accent}, ${secondary})` }}
            >
              Open Knowledge Manager →
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
